import { z } from 'zod';
import { Email, IsoDateTime, Slug, Uuid } from './common.js';
import { TenantStatus, UsageMetric } from './platform.js';
/**
 * Core Phase 4 shapes (docs/modules/core-phase4.md). Owners: C5 (user tokens, tenant restriction), C6 (password policy,
 * support sessions), C7 (SSO), C8 (custom domains), C9 (partner console, tenant export and closure). Architect-owned:
 * builders may add optional fields; renames and type changes go through the architect.
 */
// --- s1 User tokens (C5, ADR 0027) ----------------------------------------------------------------------------------------
/** What a user token is for. Each purpose limits which routes accept it (route config `userTokens: ['outlook']`). */
export const UserTokenPurpose = z.enum(['outlook']);
export const UserToken = z.object({
    id: Uuid,
    purpose: UserTokenPurpose,
    /** Shown to the user, e.g. "Outlook on Sam's laptop". */
    label: z.string(),
    createdAt: IsoDateTime,
    lastUsedAt: IsoDateTime.nullable(),
    expiresAt: IsoDateTime,
});
export const UserTokenCreate = z.object({
    purpose: UserTokenPurpose,
    label: z.string().trim().min(1).max(80),
});
/** The secret is shown once: `cst_<id>.<secret>`, presented as `Authorization: User cst_...`. */
export const UserTokenWithSecret = UserToken.extend({ token: z.string() });
/** Billing restriction on a tenant (ADR 0031), enforced by the core request hook (C5). Independent of `TenantStatus`. */
export const TenantRestriction = z.enum(['none', 'kiosk_grace', 'read_only']);
// --- s2 Password policy (C6) ------------------------------------------------------------------------------------------------
/** GET /auth/password-policy (session access): the rules for the current login, so the change-password page can show them. */
export const PasswordPolicyView = z.object({
    minLength: z.number().int(),
    reuseCount: z.number().int(),
    /** Null when the password never expires for this login. */
    expiresAt: IsoDateTime.nullable(),
});
/** POST /auth/password-change (stage `password_change`): the expired password and a new one. */
export const ExpiredPasswordChange = z.object({
    currentPassword: z.string().min(1).max(256),
    newPassword: z.string().min(8).max(256),
});
// --- s3 Support access (C6) -----------------------------------------------------------------------------------------------
export const SupportSessionStart = z.object({
    /** Why support needs access; stored in the audit log and shown to the tenant. */
    reason: z.string().trim().min(5).max(500),
    minutes: z.number().int().min(5).max(240).default(30),
});
export const SupportSessionInfo = z.object({
    tenantId: Uuid,
    tenantName: z.string(),
    startedAt: IsoDateTime,
    endsAt: IsoDateTime,
    supportLabel: z.string(),
    reason: z.string(),
});
// --- s4 SSO (C7, ADR 0028) ---------------------------------------------------------------------------------------------------
export const SsoMode = z.enum(['off', 'optional', 'required']);
export const SsoConfig = z.object({
    provider: z.literal('entra'),
    mode: SsoMode,
    /** The customer's Entra directory (tenant) id, set by the "Connect" step. Null until connected. */
    directoryId: Uuid.nullable(),
    /** Email domains that may use Microsoft sign-in for this tenant (lower case). Empty = any email of an existing user. */
    domains: z.array(z.string().trim().toLowerCase().min(3).max(253)).max(20),
    /** When mode is 'required', Administrators may still sign in with password + two-factor (break-glass). */
    breakGlassAdmins: z.boolean(),
    /** Microsoft's own sign-in (and its MFA) counts as two-factor for this tenant. */
    satisfiesTwoFactor: z.boolean(),
    connectedAt: IsoDateTime.nullable(),
    updatedAt: IsoDateTime.nullable(),
    /**
     * FX2 (ADR 0028 amendment): proof of each listed domain. Discovery and required mode use only verified domains. The
     * customer publishes a TXT record `recordName` with the value `recordValue`, then asks for a check.
     */
    domainStatus: z
        .array(z.object({ domain: z.string(), recordName: z.string(), recordValue: z.string(), verifiedAt: IsoDateTime.nullable() }))
        .optional(),
});
/** POST /sso/domains/verify (FX2): checks the domain's TXT record now. Answers the new SsoConfig. */
export const SsoDomainVerifyRequest = z.object({ domain: z.string().trim().toLowerCase().min(3).max(253) });
export const SsoConfigUpdate = z.object({
    mode: SsoMode.optional(),
    domains: z.array(z.string().trim().toLowerCase().min(3).max(253)).max(20).optional(),
    breakGlassAdmins: z.boolean().optional(),
    satisfiesTwoFactor: z.boolean().optional(),
});
/** POST /auth/sso/start (public): where to send the browser. `email` picks the tenant(s) by domain; `tenantSlug` by host. */
export const SsoStartRequest = z.object({ email: Email.optional(), tenantSlug: Slug.optional() });
export const SsoStartResponse = z.object({ url: z.string() });
/** GET /auth/sso/discover?email= (public, rate-limited): whether the login page should offer "Sign in with Microsoft". */
export const SsoDiscoverResponse = z.object({ available: z.boolean(), required: z.boolean() });
// --- s5 Custom domains (C8, ADR 0030) --------------------------------------------------------------------------------------
export const DomainKind = z.enum(['subdomain', 'custom']);
export const DomainStatus = z.enum(['pending_dns', 'verified', 'failed', 'disabled']);
export const TenantDomain = z.object({
    id: Uuid,
    hostname: z.string(),
    kind: DomainKind,
    /** What it serves: the portal and contactless/forms pages share one host for custom domains. */
    primary: z.boolean(),
    status: DomainStatus,
    /** TXT record the customer must add: name and value. */
    verifyRecordName: z.string(),
    verifyRecordValue: z.string(),
    /** CNAME the customer must add (the platform's `domains.cnameTarget`). */
    cnameTarget: z.string(),
    lastCheckedAt: IsoDateTime.nullable(),
    verifiedAt: IsoDateTime.nullable(),
    errorKey: z.string().nullable(),
    createdAt: IsoDateTime,
});
export const Hostname = z
    .string()
    .trim()
    .toLowerCase()
    .min(4)
    .max(253)
    .regex(/^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/);
export const TenantDomainCreate = z.object({ hostname: Hostname });
export const TenantDomainUpdate = z.object({ primary: z.boolean().optional() });
/** GET /api/v1/branding/host (public): what the portal and public pages need for the host they were opened on. */
export const HostBranding = z.object({
    tenantSlug: Slug.nullable(),
    displayName: z.string(),
    logoUrl: z.string().nullable(),
    primaryColour: z.string(),
    accentColour: z.string(),
});
// --- s6a Customer users on the platform console (C10) -------------------------------------------------------------------------
/** One portal user of a customer, as Oakford's platform console lists them. No 2FA secrets, no password data. */
export const PlatformTenantUser = z.object({
    userId: Uuid,
    accountId: Uuid,
    fullName: z.string(),
    email: z.string(),
    roleName: z.string(),
    status: z.enum(['invited', 'active', 'disabled']),
    /** The account is locked after failed sign-ins (until this time). */
    lockedUntil: IsoDateTime.nullable(),
    twoFactorEnabled: z.boolean(),
    lastLoginAt: IsoDateTime.nullable(),
    invitedAt: IsoDateTime.nullable(),
});
export const PlatformTenantUserList = z.object({ items: z.array(PlatformTenantUser) });
// --- s6 Partner console and tenant lifecycle (C9) ---------------------------------------------------------------------------
export const TenantOverview = z.object({
    id: Uuid,
    name: z.string(),
    slug: Slug,
    status: TenantStatus,
    restriction: TenantRestriction,
    sites: z.number().int(),
    kiosks: z.number().int(),
    kiosksOffline: z.number().int(),
    users: z.number().int(),
    enabledModules: z.array(z.string()),
    /** This month's usage counters. */
    usage: z.array(z.object({ metric: UsageMetric, value: z.number() })),
    /** Worst health of the tenant's devices and jobs: 'ok' | 'warning' | 'problem'. */
    health: z.enum(['ok', 'warning', 'problem']),
    supportAccessAllowed: z.boolean(),
    closure: z.object({ requestedAt: IsoDateTime, deleteAfter: IsoDateTime }).nullable(),
    domains: z.array(z.string()),
    /** Text message limit and use this month (UK time). */
    sms: z.object({ limit: z.number().int(), used: z.number().int(), month: z.string() }).optional(),
});
export const TenantExportStatus = z.enum(['queued', 'running', 'ready', 'failed', 'expired']);
export const TenantExport = z.object({
    id: Uuid,
    status: TenantExportStatus,
    requestedAt: IsoDateTime,
    readyAt: IsoDateTime.nullable(),
    expiresAt: IsoDateTime.nullable(),
    sizeBytes: z.number().int().nullable(),
    /** Short-lived signed download link once ready. */
    downloadUrl: z.string().nullable(),
});
export const TenantClosureRequest = z.object({
    /** Typed tenant slug, to confirm. */
    confirmSlug: Slug,
    reason: z.string().trim().min(1).max(500),
    /** Override the platform notice period (days); never shorter than 7. */
    noticeDays: z.number().int().min(7).max(365).optional(),
});
export const TenantClosure = z.object({
    tenantId: Uuid,
    requestedAt: IsoDateTime,
    deleteAfter: IsoDateTime,
    exportId: Uuid.nullable(),
    cancelledAt: IsoDateTime.nullable(),
    deletedAt: IsoDateTime.nullable(),
});
//# sourceMappingURL=core-p4.js.map